/**
 * OCT28 #6: the shop's primary advisors, one cached read per shop (RLS: shop members read
 * ticket_advisor_assignments; nobody writes it from the browser). Keyed by the LOGICAL repair order — the chain head.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCustodyActorNames } from '@/hooks/tickets/useCustodyActorNames';
import { UNKNOWN_MEMBER_LABEL } from '@/lib/people/personDisplayName';

export interface TicketAdvisor { userId: string; name: string | null }

export function useShopAdvisorAssignments(shopId: string | null | undefined) {
  return useQuery({
    queryKey: ['shop-advisor-assignments', shopId],
    enabled: !!shopId,
    staleTime: 60_000,
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await (supabase as any)
        .from('ticket_advisor_assignments')
        .select('ticket_id, advisor_user_id')
        .eq('shop_id', shopId)
        .not('advisor_user_id', 'is', null);
      if (error) throw error;
      return new Map(((data ?? []) as Array<{ ticket_id: string; advisor_user_id: string }>).map((r) => [r.ticket_id, r.advisor_user_id]));
    },
  });
}

/** The advisor of a ticket's repair order: its own assignment, else its chain head's (continuation days). */
export function useTicketAdvisor(shopId: string | null | undefined, ticketId: string, chainHeadId?: string | null): TicketAdvisor | null {
  const { data: assignments } = useShopAdvisorAssignments(shopId);
  const userId = assignments?.get(ticketId) ?? (chainHeadId ? assignments?.get(chainHeadId) : undefined) ?? null;
  // The canonical person-name rule (roster → professional identity → account name, never an email), for ANY viewer
  // of the shop — the front-office member list is feature-gated and empty for technicians (browser smoke, RC1).
  const { data: names } = useCustodyActorNames(userId ? [userId] : [], shopId ?? null);
  return useMemo(() => {
    if (!userId) return null;
    const name = names?.[userId];
    return { userId, name: name && name !== UNKNOWN_MEMBER_LABEL ? name : null };
  }, [userId, names]);
}
